import { useEffect, useState } from 'react';
import { List, Plus, RefreshCw, Trash2, Users, ChevronRight } from 'lucide-react';
import api from '../services/api';

interface SmartList {
  id: number;
  name: string;
  description: string | null;
  filter_sql: string;
  is_system: boolean;
  contact_count: number;
  last_refreshed: string | null;
}

interface SmartListContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  title: string | null;
  status: string;
  lifecycle_stage: string;
  lead_score: number;
  warmth: number;
  last_interaction: string | null;
  subscription_status: string | null;
}

export default function SmartListsPage() {
  const [lists, setLists] = useState<SmartList[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState<number | null>(null);
  const [contacts, setContacts] = useState<SmartListContact[]>([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFilter, setNewFilter] = useState('');
  const [newDesc, setNewDesc] = useState('');

  const fetchLists = async () => {
    setLoading(true);
    try {
      const res = await api.get('/smart-lists');
      setLists(res.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (listId: number) => {
    setContactsLoading(true);
    setSelectedList(listId);
    try {
      const res = await api.get(`/smart-lists/${listId}`);
      setContacts(res.data.contacts || []);
      setContactsTotal(res.data.total || 0);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await api.post('/smart-lists/refresh');
      await fetchLists();
    } catch {
      /* ignore */
    } finally {
      setRefreshing(false);
    }
  };

  const createList = async () => {
    if (!newName.trim() || !newFilter.trim()) return;
    try {
      await api.post('/smart-lists', { name: newName, filter_sql: newFilter, description: newDesc || null });
      setShowCreate(false);
      setNewName('');
      setNewFilter('');
      setNewDesc('');
      await fetchLists();
    } catch {
      /* ignore */
    }
  };

  const deleteList = async (id: number) => {
    try {
      await api.delete(`/smart-lists/${id}`);
      if (selectedList === id) {
        setSelectedList(null);
        setContacts([]);
      }
      await fetchLists();
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchLists();
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display" style={{ color: 'var(--text-primary)' }}>Smart Lists</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Dynamic contact segments that update automatically
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={refreshAll}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
            style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Refresh Counts
          </button>
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
            style={{ background: 'var(--netrun-green)', color: '#000' }}
          >
            <Plus size={14} /> New List
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="List name"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
          />
          <input
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
          />
          <input
            value={newFilter}
            onChange={(e) => setNewFilter(e.target.value)}
            placeholder="Filter SQL (e.g., lead_score > 50 AND status = 'active')"
            className="w-full px-3 py-2 rounded-lg text-sm font-mono"
            style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-primary)' }}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setShowCreate(false)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{ color: 'var(--text-muted)' }}
            >
              Cancel
            </button>
            <button
              onClick={createList}
              className="px-4 py-1.5 rounded-lg text-sm font-medium"
              style={{ background: 'var(--netrun-green)', color: '#000' }}
            >
              Create
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Smart Lists sidebar */}
        <div className="space-y-2">
          {loading ? (
            <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>Loading...</div>
          ) : lists.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
              No smart lists yet. Create one or run seed.
            </div>
          ) : (
            lists.map((sl) => (
              <div
                key={sl.id}
                onClick={() => fetchContacts(sl.id)}
                className="p-3 rounded-xl cursor-pointer transition-colors flex items-center gap-3"
                style={{
                  background: selectedList === sl.id ? 'rgba(144,185,171,0.15)' : 'var(--bg-secondary)',
                  border: `1px solid ${selectedList === sl.id ? 'var(--netrun-green)' : 'var(--border-primary)'}`,
                }}
              >
                <List size={16} style={{ color: sl.is_system ? 'var(--netrun-green)' : 'var(--text-muted)', flexShrink: 0 }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{sl.name}</div>
                  {sl.description && (
                    <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{sl.description}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-primary)', color: 'var(--netrun-green)' }}>
                    {sl.contact_count}
                  </span>
                  {!sl.is_system && (
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteList(sl.id); }}
                      className="p-1 rounded hover:bg-white/5"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                  <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Contacts list */}
        <div className="md:col-span-2">
          {selectedList === null ? (
            <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>
              <Users size={48} className="mx-auto mb-3 opacity-30" />
              Select a smart list to view matching contacts
            </div>
          ) : contactsLoading ? (
            <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>Loading contacts...</div>
          ) : (
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-primary)' }}>
              <div className="p-3 flex justify-between items-center" style={{ background: 'var(--bg-secondary)' }}>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {contactsTotal} contact{contactsTotal !== 1 ? 's' : ''}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2 hidden md:table-cell">Email</th>
                    <th className="text-left px-3 py-2 hidden md:table-cell">Title</th>
                    <th className="text-right px-3 py-2">Warmth</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
                        No contacts match this filter
                      </td>
                    </tr>
                  ) : (
                    contacts.map((c) => (
                      <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>
                          {c.first_name} {c.last_name}
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell" style={{ color: 'var(--text-muted)' }}>
                          {c.email}
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell" style={{ color: 'var(--text-muted)' }}>
                          {c.title}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span
                            className="text-xs font-mono px-2 py-0.5 rounded-full"
                            style={{
                              background: c.warmth > 0.6 ? 'rgba(239,68,68,0.15)' : c.warmth > 0.3 ? 'rgba(245,158,11,0.15)' : 'rgba(107,114,128,0.15)',
                              color: c.warmth > 0.6 ? '#ef4444' : c.warmth > 0.3 ? '#f59e0b' : '#6b7280',
                            }}
                          >
                            {Math.round(c.warmth * 100)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
